'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggleTheme: () => {},
  isDark: false,
});

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const supabase = createClient();
  const [theme, setTheme] = useState<ThemeMode>('light');
  const [loaded, setLoaded] = useState(false);

  // Detect system preference
  const getSystemTheme = (): ThemeMode => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Apply theme to <html>
  const applyTheme = useCallback((t: ThemeMode) => {
    if (typeof document === 'undefined') return;
    if (t === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Load preference from Supabase or localStorage
  useEffect(() => {
    async function loadPreference() {
      if (user?.id) {
        const { data } = await supabase
          .from('user_preferences')
          .select('dark_mode')
          .eq('user_id', user.id)
          .single();

        if (data) {
          const t: ThemeMode = data.dark_mode ? 'dark' : 'light';
          setTheme(t);
          applyTheme(t);
          setLoaded(true);
          return;
        }
      }

      // Fallback: localStorage or system
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('cfva-theme') : null;
      if (stored === 'dark' || stored === 'light') {
        setTheme(stored);
        applyTheme(stored);
      } else {
        const sys = getSystemTheme();
        setTheme(sys);
        applyTheme(sys);
      }
      setLoaded(true);
    }

    loadPreference();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Listen for system changes when no user preference is set
  useEffect(() => {
    if (!loaded) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!user?.id) {
        const t: ThemeMode = e.matches ? 'dark' : 'light';
        setTheme(t);
        applyTheme(t);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [loaded, user?.id, applyTheme]);

  const toggleTheme = useCallback(async () => {
    const next: ThemeMode = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('cfva-theme', next);
    }

    if (user?.id) {
      // Upsert preference in Supabase
      await supabase
        .from('user_preferences')
        .upsert(
          { user_id: user.id, dark_mode: next === 'dark' },
          { onConflict: 'user_id' }
        );
    }
  }, [theme, user?.id, applyTheme, supabase]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}
